/**
 * The latest run of one sync. Backed by `listSyncRuns` with `limit: 1` — there is no `/latest`
 * route (see `src/rpc/procedures/activations.ts`), so "the newest run" is just the smallest page of
 * the same call the past-runs table uses.
 */
import { useQuery } from '@tanstack/react-query';

import { useOrpc } from '@/_shared/hooks/useOrpc';

import type { RawSyncRun } from '../types';

export interface LatestRunQuery {
  run: RawSyncRun | undefined;
  isLoading: boolean;
  error: Error | null;
  /** `dataUpdatedAt` of the last SUCCESSFUL fetch; 0 before the first. Drives the state machine. */
  settledAt: number;
  refetch: () => void;
}

/**
 * `pollMs` is the adaptive interval the caller computes (see `useRunSync`'s `runPollIntervalMs`);
 * pass 0 (or any falsy value) to stop polling — TanStack Query treats a `0` interval the same as
 * `false`, so no translation is needed here.
 */
export function useLatestRun(syncId: string, pollMs: number): LatestRunQuery {
  const orpc = useOrpc();
  const query = useQuery({
    ...orpc.activations.listSyncRuns.queryOptions({ input: { syncId, limit: 1 } }),
    enabled: Boolean(syncId),
    refetchInterval: pollMs,
  });

  return {
    run: query.data?.runs[0],
    isLoading: query.isLoading,
    error: query.error,
    settledAt: query.isSuccess ? query.dataUpdatedAt : 0,
    refetch: () => void query.refetch(),
  };
}
